<template>
    <tailwind-layout title="User Assets Verification">
        <template #header>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto hidden lg:flex justify-between items-center"><!-- Block for lg and larger -->
                <div class="flex flex-col">
                    <h1 class="font-semibold text-2xl leading-tight">User Assets Verification</h1>
                    <h2 class="text-xs">Please add your financial institution(s)</h2>
                </div>
                <div class="flex items-center space-x-4">
                    <button v-if="!application_addon" @click="createLinkToken('auth')"
                            class="whiteButton">
                        Add New Bank Account
                    </button>
                    <a v-if="application_addon"
                       :href="route('user-assets.view-report', application_addon.asset_report_token)"
                       target="_blank"
                       class="whiteButton">
                        View Asset Report
                    </a>
                    <inertia-link class="whiteButton" :href="route('application.steps', application.id)">
                        <FontAwesome :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                    </inertia-link>
                </div>
            </div>

            <div class="mb-3 px-6 lg:px-8 mx-auto flex flex-col lg:hidden"><!-- Block for sm, md, lg (buttons will stack on smaller screens) -->
                <div class="flex flex-col">
                    <h1 class="font-semibold text-2xl leading-tight">User Assets Verification</h1>
                    <h2 class="text-xs">Please add your financial institution(s)</h2>
                </div>
                <div class="flex justify-between items-start md:items-center">
                    <button v-if="!application_addon" @click="createLinkToken('auth')"
                            class="whiteButton">
                        Add New Bank Account
                    </button>
                    <a v-if="application_addon"
                       :href="route('user-assets.view-report', application_addon.asset_report_token)"
                       target="_blank"
                       class="whiteButton">
                        View Asset Report
                    </a>
                    <inertia-link class="whiteButton" :href="route('application.steps', application.id)">
                        <FontAwesome :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                    </inertia-link>
                </div>
            </div>
        </template>
        <div class="rounded-md lg:shadow shadow-none overflow-x-auto">
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="shadow overflow-hidden lg:border">
                                <table class="min-w-full divide-y divide-gray-300">
                                    <thead class="bg-blue-50">
                                    <tr>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Account Last 4 Digits
                                        </th>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Bank Name
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="account in accounts" :key="account.id"
                                        class="bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer">
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{ account.account_last_4 }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{ account.bank_name }}
                                        </td>
                                    </tr>
                                    <tr v-show="!accounts.length">
                                        <td colspan="3"
                                            class="px-6 py-4 whitespace-nowrap text-dark-gray-400 text-sm font-medium text-center">
                                            No bank accounts were found.
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <alert-card v-if="!application_addon" :type="'warning'">
                Once you've finished adding your financial institutions, click <a :href="route('user-assets.create-report', application.id)" class="text-blue-600 underline">Create Assets Report</a>.
            </alert-card>
        <full-page-loader v-if="isLoader"></full-page-loader>
    </tailwind-layout>
</template>

<script>
import Icon from '@/Shared/Icon.vue'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination.vue'
import pickBy from 'lodash/pickBy'
import Button from "@/Jetstream/Button.vue";
import axios from "axios";
import FullPageLoader from "@/Shared/FullPageLoader.vue";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons'
import { router } from '@inertiajs/vue3'
import AlertCard from "@/Shared/AlertCard.vue";

export default {
    components: {
        FullPageLoader,
        Button,
        Icon,
        Pagination,
        TailwindLayout,
        FontAwesome: FontAwesomeIcon,
        AlertCard
    },
    props: {
        accounts: Object,
        application: Object,
        application_addon: Object,
    },
    data() {
        return {
            isLoader: false,
            faChevronLeft,
        }

    },
    methods: {
        async createLinkToken(product) {
            this.isLoader = true;
            try {
                const response = await axios.post(route('user-assets.create-link-token'), {
                    product: product,
                });
                if (response.data.status) {
                    this.initializePlaidLink(response.data.public_token, product);
                }

                // Handle the successful response here
            } catch (error) {
                // Handle any errors here
                console.error('Error:', error.response ? error.response.data : error.message);
            }
        },

        async initializePlaidLink(public_token, product) {
            this.isLoader = false;
            var handler = Plaid.create({
                token: public_token,  // Use the provided public token directly
                onLoad: function () {
                    // Optional, called when Link loads
                },
                onSuccess: (public_token, metadata) => {
                    this.isLoader = true;
                    if (product === 'auth') {
                        this.finishAuth(public_token, metadata);
                    }
                },
                onExit: function (err, metadata) {
                    // The user exited the Link flow.
                    if (err != null) {
                        // The user encountered a Plaid API error prior to exiting.
                    }
                    // metadata contains information about the institution
                    // that the user selected and the most recent API request IDs.
                    // Storing this information can be helpful for support.
                },
                onEvent: function (eventName, metadata) {
                    // Optionally capture Link flow events, streamed through
                    // this callback as your users connect an Item to Plaid.
                    // For example:
                    // eventName = "TRANSITION_VIEW"
                    // metadata  = {
                    //   link_session_id: "123-abc",
                    //   mfa_type:        "questions",
                    //   timestamp:       "2017-09-14T14:42:19.350Z",
                    //   view_name:       "MFA",
                    // }
                }
            });

            handler.open();
        },

        async finishAuth(public_token, metadata) {
            const response = await axios.post(route('user-assets.exchange-token-and-retrieve-account'), {
                public_token: public_token,
                metadata: metadata,
            });
            this.isLoader = false;
            router.reload();
        },
    },
}
</script>
